import React, { useState, useEffect } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import axios from '../api/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import { useProjectTypes } from '../utils/projectTypes';
import researchSplash from '../assets/rdh-splash.jpg';
import './Splash.css';

export default function Splash({ onCreateProject, onViewProjects, onViewProjectsByType, onViewSites, onResearchLocation }) {
  const { canCreateProject } = useAuth();
  const projectTypes = useProjectTypes();
  const [stats, setStats] = useState({ projects: 0, sites: 0, projectsByType: {}, loading: true });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [projectStatsRes, sitesRes] = await Promise.all([
          axios.get('/api/v1/projects/stats').catch(() => ({ data: { data: { total: 0, byType: {} } } })),
          axios.get('/api/v1/sites', { params: { limit: 0 } }).catch(() => ({ data: { data: [], meta: { total: 0 } } }))
        ]);
        
        const projectStats = projectStatsRes.data?.data || { total: 0, byType: {} };
        
        setStats({
          projects: projectStats.total,
          sites: sitesRes.data?.meta?.total ?? 0,
          projectsByType: projectStats.byType,
          loading: false
        });
      } catch (error) {
        setStats({ projects: 0, sites: 0, projectsByType: {}, loading: false });
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="splash-page">
      {/* Hero Section */}
      <div className="splash-hero" role="img" aria-label="Research Data Hub hero" style={{ backgroundImage: `url(${researchSplash})` }}>
        <div className="splash-hero-overlay">
          <div className="splash-hero-content">
            <p className="hero-description">
              Search, explore, and manage LPC's Research Department data. Create and analyze survey data, SHPO CRIS Data, LPC environmental review historic resource determinations, and Request for Evaluation (RFE) determinations.
            </p>
            <div className="hero-stats">
              <div className="stat-item-projects" onClick={onViewProjects}>
                <div className="stat-number-projects">
                  <Icon name="folder open" />
                  {stats.loading ? '...' : stats.projects}
                </div>
                <div className="stat-label-projects">
                  Surveys <Icon name="arrow right" size="small" />
                </div>
                {!stats.loading && Object.keys(stats.projectsByType).length > 0 && (
                  <div className="stat-breakdown">
                    {Object.entries(stats.projectsByType).map(([type, count]) => {
                      const t = projectTypes.find(x => x.type_cd === type);
                      const label = (t && (t.description || t.type_label)) || type;
                      return (
                        <div key={type} className="stat-breakdown-item stat-breakdown-link" onClick={(e) => { e.stopPropagation(); onViewProjectsByType(type); }}>
                          <span className="breakdown-count">{count}</span> {label} <Icon name="arrow right" size="small" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="stat-item-sites" onClick={onViewSites}>
                <div className="stat-number-sites">
                  <Icon name="map marker alternate" />
                  {stats.loading ? '...' : stats.sites.toLocaleString()}
                </div>
                <div className="stat-label-sites">
                  Buildings <Icon name="arrow right" size="small" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="splash-actions">
        <div className="actions-header">
          <h2 className="actions-title">Get Started</h2>
          <p className="actions-subtitle">Choose an action to begin working with the Research Data Hub</p>
        </div>

        <div className="actions-grid">
          <div className="action-card primary" onClick={onResearchLocation}>
            <div className="action-icon-wrapper">
              <Icon name="search" className="action-icon" />
            </div>
            <h3 className="action-title">Search</h3>
            <p className="action-description">
              Look up an address, BBL, or BIN to find projects, site data,
              and enrichment information.
            </p>
            <Button primary className="action-button">
              Search <Icon name="arrow right" />
            </Button>
          </div>

          {canCreateProject() && (
          <div className="action-card" onClick={onCreateProject}>
            <div className="action-icon-wrapper">
              <Icon name="plus circle" className="action-icon" />
            </div>
            <h3 className="action-title">Create Survey</h3>
            <p className="action-description">
              Add a new research survey to the database. Define survey details,
              location, and metadata.
            </p>
            <Button className="action-button">
              Create Survey <Icon name="arrow right" />
            </Button>
          </div>
          )}

          <div className="action-card" onClick={onViewProjects}>
            <div className="action-icon-wrapper">
              <Icon name="folder open" className="action-icon" />
            </div>
            <h3 className="action-title">View Surveys</h3>
            <p className="action-description">
              Browse and manage existing research surveys. Edit survey details,
              view associated buildings, and manage survey data.
            </p>
            <Button className="action-button">
              View Surveys <Icon name="arrow right" />
            </Button>
          </div>

          <div className="action-card" onClick={onViewSites}>
            <div className="action-icon-wrapper">
              <Icon name="map marker alternate" className="action-icon" />
            </div>
            <h3 className="action-title">View Buildings</h3>
            <p className="action-description">
              Explore all landmark buildings in the database. View building details,
              attributes, and geographic information.
            </p>
            <Button className="action-button">
              View Buildings <Icon name="arrow right" />
            </Button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="splash-features">
        <div className="features-grid">
          <div className="feature-item">
            <Icon name="search" className="feature-icon" />
            <h4>Search & Explore</h4>
            <p>Powerful query tools to search and filter research data</p>
          </div>
          <div className="feature-item">
            <Icon name="database" className="feature-icon" />
            <h4>Data Management</h4>
            <p>Manage surveys, buildings, and attributes with ease</p>
          </div>
          <div className="feature-item">
            <Icon name="map" className="feature-icon" />
            <h4>Geographic Data</h4>
            <p>View and manage spatial information for landmarks</p>
          </div>
          <div className="feature-item">
            <Icon name="chart bar" className="feature-icon" />
            <h4>Analytics</h4>
            <p>Analyze research data with custom queries and reports</p>
          </div>
        </div>
      </div>
    </div>
  );
}
